import type { VcnWexCardDto } from 'enrollment-platform/api/vcn'
import type { PartnerVcnWexShowPanResponseDto } from 'partner-payment-service/api/vcn'

import { VirtualCardType } from './constants'

/**
 * Marqeta-style virtual card fields (matches financeService `VirtualCard` payload)
 * after mapping from EPS WEX provision response.
 */
export interface VirtualCardPayload {
  // financeService `VirtualCard` payload fields
  id?: string
  pan?: string
  cvv_number?: string
  expiration_time?: string
  firstName?: string
  lastName?: string
  state?: string
  token?: string
  monthly_card_limit?: number | '-'
  created_time?: string
  last_modified_time?: string
  card_public_key?: string
  last_four?: string
  // Partner Payment Service `showpan` response additional fields
  vcnProvider?: string
  // WEX cardholder name from `userDefinedFields.fullname` (the actual covered person,
  // which may be a dependent on a split benefit — not necessarily the employee).
  cardholderName?: string
}

function mapMonthlyCardLimit(
  payment?: { creditLimit?: number; creditLimitPerMonth?: number } | null
): number | '-' | undefined {
  const perMonth = payment?.creditLimitPerMonth
  if (perMonth != null && perMonth < 0) {
    return '-'
  }
  const limit = perMonth != null ? perMonth : payment?.creditLimit
  return limit != null ? limit : undefined
}

/**
 * Maps EPS WEX card wire payload to the legacy finance virtual-card shape
 * expected by virtual-cards Redux and UI.
 *
 * `creditLimitPerMonth` (or `creditLimit` when absent) is treated as minor units
 * (e.g. cents) when mapping to `monthly_card_limit`. A negative `creditLimitPerMonth`
 * maps to `'-'` for display.
 */
export function mapWexVcnProvisionToFinanceVirtualCard(
  res: VcnWexCardDto
): VirtualCardPayload {
  const { publicKey, merchantLogId, status, rawData } = res
  const vc = rawData.virtualCard
  const payment = rawData.virtualCardPayment
  const monthlyCardLimit = mapMonthlyCardLimit(payment)

  const expirationRaw = vc?.expiration
  const hasNoTimezone = expirationRaw && !expirationRaw.includes('T')
  const expirationTime = hasNoTimezone
    ? `${expirationRaw}T00:00:00.000Z`
    : expirationRaw

  return {
    id: rawData.id,
    vcnProvider: VirtualCardType.WEX,
    card_public_key: publicKey,
    token: merchantLogId,
    state: status?.toUpperCase(),
    pan: vc?.number,
    cvv_number: vc?.securityCode,
    expiration_time: expirationTime,
    last_four: vc?.last4,
    monthly_card_limit: monthlyCardLimit,
    created_time: rawData.createdDate,
    last_modified_time: rawData.modifiedDate,
    cardholderName: rawData.userDefinedFields?.fullname ?? undefined,
  }
}

/**
 * Maps Partner Payment Service show-PAN payload to the legacy finance virtual-card shape.
 *
 * `epsVcnStatus` must be the EPS card `status` (same source as {@link mapWexVcnProvisionToFinanceVirtualCard});
 * show-PAN `paymentStatus` is not the VCN lifecycle state.
 */
export function mapPartnerVcnWexShowPanToFinanceVirtualCard(
  res: PartnerVcnWexShowPanResponseDto,
  epsVcnStatus?: string
): VirtualCardPayload {
  const vc = res.virtualCard
  const payment = res.virtualCardPayment
  const monthlyCardLimit = mapMonthlyCardLimit(payment)

  const expirationRaw = vc?.expiration
  const expirationTime =
    expirationRaw && !expirationRaw.includes('T')
      ? `${expirationRaw}T00:00:00.000Z`
      : expirationRaw

  return {
    id: res.id,
    vcnProvider: VirtualCardType.WEX,
    card_public_key: res.id,
    token: res.id,
    state: epsVcnStatus?.toUpperCase(),
    pan: vc?.number,
    cvv_number: vc?.securityCode,
    expiration_time: expirationTime,
    last_four: vc?.last4,
    monthly_card_limit: monthlyCardLimit,
    created_time: res.createdDate,
    last_modified_time: res.modifiedDate,
    cardholderName: res.userDefinedFields?.fullname ?? undefined,
  }
}
